/**
 * toolTheme — derive a small palette from a tool's accent colour.
 *
 * Every tool in the dashboard declares a `color` (its tint — see each
 * src/tools/*Tool.js). This formalises that single colour into a set of
 * tints/shades + semantic roles so a tool can colour its charts, KPIs and
 * containers consistently without hand-picking hex values everywhere.
 *
 * Usage (component): const t = createToolTheme(tool.color); <TrendChart color={t.color} />
 * Usage (CSS vars):  <div style={toolThemeVars(tool.color)}> … var(--tool-color) … </div>
 *   (ToolPage does this automatically when given the `themed` prop.)
 */

const DEFAULT = "#6366f1"; // indigo — matches the app's historical accent

/**
 * The shared neutral for tools whose CONTENT is colour-coded.
 *
 * A tool that uses red/amber/green to mean something must not also tint its
 * buttons, filters and charts with an accent that competes — System Health's
 * rose made every control read as "danger" on a board where red is reserved
 * for failure. Such a tool sets `controlColor = "neutral"` and keeps its own
 * `color` for identity (sidebar, dashboard tile, page-header icon), so it is
 * still recognisable without its interface pre-judging its contents.
 */
export const NEUTRAL_CONTROL = "#475569"; // slate

/**
 * Resolve a tool's CONTROL colour — the tint for buttons, filters and charts,
 * as distinct from its IDENTITY colour. Accepts a hex, the keyword "neutral",
 * or nothing (in which case identity and controls are the same, as for every
 * tool that has no reason to separate them).
 */
export function resolveControlColor(tool) {
  const c = tool?.controlColor;
  if (!c) return tool?.color;
  return c === "neutral" ? NEUTRAL_CONTROL : c;
}
const WHITE = { r: 255, g: 255, b: 255 };
const BLACK = { r: 0, g: 0, b: 0 };

function clampByte(n) {
  return Math.max(0, Math.min(255, Math.round(n)));
}

function parseHex(hex) {
  let h = String(hex || "")
    .replace("#", "")
    .trim();
  if (h.length === 3)
    h = h
      .split("")
      .map((c) => c + c)
      .join("");
  if (h.length !== 6 || /[^0-9a-fA-F]/.test(h)) return null;
  const num = parseInt(h, 16);
  return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
}

function toHex({ r, g, b }) {
  return (
    "#" +
    [r, g, b].map((x) => clampByte(x).toString(16).padStart(2, "0")).join("")
  );
}

// Mix `hex` toward `target` by `weight` (0..1).
function mix(hex, target, weight) {
  const c = parseHex(hex) || parseHex(DEFAULT);
  return toHex({
    r: c.r + (target.r - c.r) * weight,
    g: c.g + (target.g - c.g) * weight,
    b: c.b + (target.b - c.b) * weight,
  });
}

export function lighten(hex, weight) {
  return mix(hex, WHITE, weight);
}

export function darken(hex, weight) {
  return mix(hex, BLACK, weight);
}

/**
 * Build a tool palette from a single accent colour.
 * @param {string} hex - the tool's base colour (e.g. "#4299E1")
 * @returns {{color,dark,soft,softer,border,tint,shade}}
 */
export function createToolTheme(hex = DEFAULT) {
  const color = parseHex(hex) ? hex : DEFAULT;
  return {
    color, //                      base accent — primary charts, active controls
    dark: darken(color, 0.16), //  hover / active / strong text
    soft: lighten(color, 0.9), //  pale background (favourite chips, accent tiles)
    softer: lighten(color, 0.95), // even paler background
    border: lighten(color, 0.62), // light border to pair with `soft`
    tint: lighten(color, 0.34), //  lighter chart shade (secondary series)
    shade: darken(color, 0.26), //  darker chart shade (secondary series)
  };
}

/**
 * CSS custom properties for a tool's palette. Spread onto a style object so
 * descendant components (incl. shared Button / SegmentedControl) can pick up
 * `var(--tool-color, …)`.
 */
export function toolThemeVars(hex) {
  const t = createToolTheme(hex);
  return {
    "--tool-color": t.color,
    "--tool-color-dark": t.dark,
    "--tool-color-soft": t.soft,
    "--tool-color-softer": t.softer,
    "--tool-color-border": t.border,
    "--tool-color-tint": t.tint,
    "--tool-color-shade": t.shade,
  };
}

export default createToolTheme;
